/**
 * @file 插件钱包领取质押奖励
 */
import { Button, Modal } from '@bfe/moly';
import { useFm } from '@web3/base-hooks';
import { TipModal, Loading, Row } from '@/components';
import { Process, ClaimType } from '@/types/claim';
import { useGasFee, useClaim, useButtonStatus, useClaimStage } from './hooks';
import { useProcess, useBalance, useRewardList } from '../hooks';
import { ClaimContent, OrderResultModal } from '../components';
import { PoolInfo } from '@/types/pool';
import { formatUSDYield } from '@/utils/num';
import { scTrack } from '@web3/base-tracking';
import { Event } from '@/config/track-config';
import { RiskNotification } from '@/containers/operation/components/riskNotification';

import styles from './index.module.scss';

interface Props {
  poolInfo: PoolInfo;
  claimType: ClaimType;
  onCloseClaimModal: () => void;
}

export const ThirdClaimModal = (props: Props) => {
  const { claimType, poolInfo, onCloseClaimModal } = props;
  const { process, onChangeProcess } = useProcess();
  const fm = useFm();
  const { claimStage, onChangeClaimStage } = useClaimStage();
  const {
    isLoading,
    rewardList,
    rewardValue,
    rewardType,
    hasReward,
    rewardFunds,
  } = useRewardList({
    poolInfo,
  });

  const { gasStore } = useGasFee({
    process,
    poolInfo,
    hasReward,
    rewardFunds,
    onChangeClaimStage,
  });

  const { balance } = useBalance({
    poolInfo,
  });

  const {
    orderCode,
    confirmButtonIsLoading,
    gasFeeIsGreatThanProfit,
    beginClaim,
  } = useClaim({
    gasStore,
    rewardValue,
    claimStage,
    process,
    poolInfo,
    claimType,
    rewardFunds,
    onChangeClaimStage,
    onChangeProcess,
  });

  const { buttonText, errorText, buttonDisabled } = useButtonStatus({
    balance,
    rewardType,
    gasStore,
    claimType,
  });

  return (
    <>
      {(process === Process.Confirm || process === Process.GasFeeTip) && (
        <Modal
          open
          title={
            claimType === ClaimType.Claim
              ? fm('claim_revenue')
              : fm('principalWithdrawal')
          }
          className={styles.container}
          footer={null}
          modal={false}
          onClose={() => {
            onCloseClaimModal();
          }}
          lockScroll={false}
        >
          {!isLoading && (
            <>
              {/* 领取收益详情内容 */}
              <ClaimContent rewardList={rewardList} rewardValue={rewardValue} />
              {/* 矿工费 */}
              <Row
                label={fm('estimated_cost')}
                className={styles.gasfee}
                show={gasStore.hasGasFee}
                value={
                  <div>
                    <div
                      className={styles.amount}
                    >{`${gasStore.amount} ${gasStore.symbol}`}</div>
                    <div className={styles.price}>
                      {`${formatUSDYield(gasStore.price)}`}
                    </div>
                  </div>
                }
              />
              {/* 领取奖励错误信息 */}
              {errorText && <div className={styles.error}>{errorText}</div>}
              {/* 领取奖励风险提示 */}
              <RiskNotification
                type="claim"
                extraClass={styles.riskContainer}
              />
              <Button
                variant="primary"
                className={styles.button}
                loading={confirmButtonIsLoading}
                disabled={buttonDisabled}
                onClick={() => {
                  if (gasFeeIsGreatThanProfit) {
                    onChangeProcess(Process.GasFeeTip);
                    return;
                  }
                  beginClaim();
                }}
              >
                {buttonText}
              </Button>
            </>
          )}
          {isLoading && <Loading />}
        </Modal>
      )}
      {/* 矿工费超过收益提示弹窗 */}
      {process === Process.GasFeeTip && (
        <TipModal
          show
          data-spm-even={Event.Web3PageView}
          data-spm-data={JSON.stringify({
            page_name: 'StakingWeb_bbSOLClaimSmallincome',
          })}
          onClose={() => {
            onChangeProcess(Process.Confirm);
          }}
          onConfirm={() => {
            scTrack(Event.Web3PageView, {
              page_name: 'StakingWeb_bbSOLClaimSmallincome',
              button_name: 'Smallincome_confirm',
            });
            beginClaim();
          }}
          content={fm('confirmClaimDueToGasFee')}
          buttonText={fm('continueClaiming')}
        ></TipModal>
      )}
      {/* 质押结果弹窗 */}
      {process === Process.Result && (
        <OrderResultModal
          show
          gasStore={gasStore}
          rewardList={rewardList}
          rewardValue={rewardValue}
          orderCode={orderCode}
          claimType={claimType}
          poolInfo={poolInfo}
        />
      )}
    </>
  );
};
